import _, { isEmpty } from 'lodash';
import _slicedToArray from "@babel/runtime-corejs3/helpers/slicedToArray";
import _JSON$stringify from "@babel/runtime-corejs3/core-js-stable/json/stringify";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";
import {
    getConfigValue
} from "@jutro/config";
import {
    joinUrl
} from "@jutro/platform";
import {
    useAuthClient
} from "./clients/useAuthClient";
import {
    AuthContext
} from "@jutro/auth/core/AuthContext";
import {
    AuthContextInternal
} from "@jutro/auth/core/AuthContextInternal";
import {
    useTokenRequestListener
} from "@jutro/auth/core/crossSiteIframeUtils";
import {
    OidcRoutes
} from "@jutro/auth/core/OidcRoutes";
import {
    OidcSession
} from "@jutro/auth/core/OidcSession";
import {
    useEnvVariablesWarnings
} from "@jutro/auth/core/useEnvVariablesWarnings";
import {
    useAuthStorage
} from "@jutro/auth/storage/useAuthStorage";
import {
    getBasePath
} from "@jutro/auth/utils/getBasePath";
import {
    mergeParamsIfExists
} from "@jutro/auth/utils/mergeParams";
var getTokens = function(oidc) {
        var tokens = oidc.getTokens();
        return null !== tokens ? {
            accessToken: tokens.accessToken,
            idToken: tokens.idToken
        } : null
    },
    getIsAuthenticated = function(oidc, oidcUser) {
        return null !== oidc.getTokens() && null !== oidcUser
    },
    getIsPending = function(oidc, oidcUser, isLoginPending, isLogoutPending) {
        return isLoginPending || isLogoutPending || null !== oidc.getTokens() && null === oidcUser
    };
export var AuthProvider = function(_ref) {
    var children = _ref.children,
        onLocationChange = _ref.onLocationChange;
    useEnvVariablesWarnings();
    var basePath = useMemo((function() {
            return getBasePath()
        }), []),
        oidc = useAuthClient(basePath),
        _useState = useState(!1),
        _useState2 = _slicedToArray(_useState, 2),
        isLoginPending = _useState2[0],
        setIsLoginPending = _useState2[1],
        _useState3 = useState(!1),
        _useState4 = _slicedToArray(_useState3, 2),
        isLogoutPending = _useState4[0],
        setIsLogoutPending = _useState4[1],
        _useState5 = useState((function() {
            return getTokens(oidc)
        })),
        _useState6 = _slicedToArray(_useState5, 2),
        tokens = _useState6[0],
        setTokens = _useState6[1],
        _useState7 = useState(null),
        _useState8 = _slicedToArray(_useState7, 2),
        oidcUser = _useState8[0],
        setOidcUser = _useState8[1],
        _useState9 = useState(void 0),
        _useState10 = _slicedToArray(_useState9, 2),
        error = _useState10[0],
        setError = _useState10[1];
    useEffect((function() {
        var hasChanged = !1;
        return oidc.registerStateListeners({
                onTokenChanged: function(newTokens) {
                    hasChanged || setTokens(newTokens)
                },
                onTokenRefreshFailure: function() {
                    hasChanged || oidc.login("", mergeParamsIfExists("JUTRO_AUTH_LOGIN_QUERY_EXTRAS", {}))
                },
                onError: function(name, err) {
                    hasChanged || setError(new Error(name, {
                        cause: _JSON$stringify(err)
                    }))
                }
            }),
            function() {
                hasChanged = !0, oidc.removeStateListeners()
            }
    }), [oidc]), useEffect((function() {
        var hasChanged = !1;
        return tokens ? isLogoutPending || oidc.getUserInfo().then((function(info) {
                hasChanged || setOidcUser(info)
            })).catch((function(err) {
                hasChanged || (setOidcUser(null), setError(err))
            })) : setOidcUser(null),
            function() {
                hasChanged = !0
            }
    }), [oidc, tokens, isLogoutPending]), useTokenRequestListener(getTokens(oidc)), useAuthStorage(tokens);
    var login = useCallback((function() {
            var callbackPath = arguments.length > 0 && void 0 !== arguments[0] ? arguments[0] : "",
                extraParams = arguments.length > 1 && void 0 !== arguments[1] ? arguments[1] : {};
            return oidc.login(callbackPath, mergeParamsIfExists("JUTRO_AUTH_LOGIN_QUERY_EXTRAS", extraParams))
        }), [oidc]),
        logout = useCallback((function(fromPath, extraParams) {
            var defaultLogoutPath = joinUrl(basePath, getConfigValue("JUTRO_AUTH_LOGOUT_REDIRECT_PATH", "/logout")),
                logoutPath = null != fromPath ? fromPath : defaultLogoutPath;
            return setIsLogoutPending(!0), oidc.logout(logoutPath, mergeParamsIfExists("JUTRO_AUTH_LOGOUT_QUERY_EXTRAS", extraParams))
        }), [oidc, basePath]),
        renewTokens = useCallback((extraParams => oidc.renewTokens(extraParams)), [oidc]),
        silentLogin = useCallback(((extraParams, scope) => oidc.silentLogin(extraParams, scope)), [oidc]),
        contextValue = useMemo((function() {
            var _tokens$accessToken, _tokens$idToken;

            // DE18279 (& KACE:177728)
            // remove '@twiaportal.org' if present for display purposes
            if(oidcUser){
              _.set(oidcUser, 'preferred_username', _.get(oidcUser, 'preferred_username','').replace("@twiaportal.org",""));
            } 

            return {
                isAuthenticated: getIsAuthenticated(oidc, oidcUser),
                isPending: getIsPending(oidc, oidcUser, isLoginPending, isLogoutPending),
                accessToken: null !== (_tokens$accessToken = null == tokens ? void 0 : tokens.accessToken) && void 0 !== _tokens$accessToken ? _tokens$accessToken : null,
                idToken: null !== (_tokens$idToken = null == tokens ? void 0 : tokens.idToken) && void 0 !== _tokens$idToken ? _tokens$idToken : null,
                userInfo: oidcUser,
                error: error,
                login: login,
                logout: logout,
                renewTokens: renewTokens,
                silentLogin: silentLogin
            }
        }), [oidc, isLoginPending, isLogoutPending, tokens, oidcUser, error, login, logout]),
        internalContextValue = useMemo((function() {
            return {
                oidc: oidc,
                setIsLoginPending: setIsLoginPending,
                setError: setError,
                onLocationChange: onLocationChange
            }
        }), [oidc, onLocationChange]);
    return React.createElement(AuthContext.Provider, {
        value: contextValue
    }, React.createElement(AuthContextInternal.Provider, {
        value: internalContextValue
    }, React.createElement(OidcRoutes, null, React.createElement(OidcSession, null, children))))
};
